import React, { useState, useMemo, useEffect } from "react";
import styled from "styled-components";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

const Wrapper = styled.div`
  padding: 20px;
  background: #f9f9f9;
  position: relative;
`;

const Filters = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
  align-items: start;
  justify-content: center;
  margin-bottom: 20px;
`;

const Picker = styled.div` display: grid; gap: 8px; `;
const Label = styled.label` font-size: 14px; color: #444; `;
const SearchInput = styled.input`
  padding: 8px 12px; font-size: 14px; border-radius: 6px; border: 1px solid #ddd; width: 100%;
`;
const Select = styled.select`
  padding: 8px 12px; font-size: 15px; border-radius: 6px; border: 1px solid #ddd; width: 100%;
`;
const Title = styled.h3`
  font-family: Arial, sans-serif; font-size: 12px; font-style: normal; font-weight: bold;
  text-align: center; color: #666; margin-bottom: 12px; position: relative;
`;
const InfoIconWrapper = styled.div`
  position: absolute; top: 10px; right: 10px; cursor: pointer; color: #666; transition: color 0.3s;
  &:hover { color: #000; } svg { width: 18px; height: 18px; }
`;
const EmptyState = styled.div`
  text-align: center; color: #777; font-size: 14px; padding: 24px 8px;
`;

// ISO-8601 week number
function getISOWeek(dateStr) {
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7; // 1..7
  date.setUTCDate(date.getUTCDate() + 4 - dayNum); // Thursday
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
  return weekNo; // 1..52/53
}

const WeeklyPurchaseByContractorChart = ({ data }) => {
  // All detected years (desc)
  const allYears = useMemo(() => {
    const set = new Set(
      data.map(d => parseInt(d.Rok)).filter(y => !Number.isNaN(y))
    );
    return Array.from(set).sort((a, b) => b - a);
  }, [data]);

  // ✅ Years with positive purchases: Σ (K_42 + K_43) > 0
  const yearsWithData = useMemo(() => {
    const totalsByYear = new Map();
    for (const d of data) {
      const y = parseInt(d.Rok);
      if (Number.isNaN(y)) continue;
      const val = (Number(d.K_42) || 0) + (Number(d.K_43) || 0);
      totalsByYear.set(y, (totalsByYear.get(y) || 0) + val);
    }
    return Array.from(totalsByYear.entries())
      .filter(([, sum]) => sum > 0)
      .map(([y]) => y)
      .sort((a, b) => b - a);
  }, [data]);

  // ✅ Initial year: latest with data -> any year -> current year
  const initialYear = yearsWithData[0] ?? allYears[0] ?? new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(initialYear);

  const [selectedSuppliers, setSelectedSuppliers] = useState([]); // init via effect
  const [searchTerms, setSearchTerms] = useState(["", "", ""]);

  // ✅ Keep selectedYear valid as data changes
  useEffect(() => {
    const latest = yearsWithData[0] ?? allYears[0] ?? new Date().getFullYear();
    const hasAnyYear = allYears.includes(selectedYear);
    const selectedHasData = yearsWithData.includes(selectedYear);
    if (!hasAnyYear || !selectedHasData) setSelectedYear(latest);
  }, [yearsWithData, allYears]); // eslint-disable-line react-hooks/exhaustive-deps

  // ✅ Only suppliers with Σ (K_42 + K_43) > 0 in selected year
  const suppliersForYearSorted = useMemo(() => {
    const totals = new Map();
    for (const d of data) {
      const y = parseInt(d.Rok);
      if (y !== selectedYear) continue;
      const name = d.NazwaDostawcy;
      if (!name) continue;
      const val = (Number(d.K_42) || 0) + (Number(d.K_43) || 0);
      totals.set(name, (totals.get(name) || 0) + val);
    }
    return [...totals.entries()]
      .filter(([, sum]) => sum > 0)
      .map(([name]) => name)
      .sort((a, b) => a.localeCompare(b, "pl", { sensitivity: "base" }));
  }, [data, selectedYear]);

  // Preserve selections on year change; autofill to min 2 (max 3)
  useEffect(() => {
    setSelectedSuppliers(prev => {
      const preserved = prev.filter(s => suppliersForYearSorted.includes(s));
      const need = Math.max(0, 2 - preserved.length);
      const fillers = suppliersForYearSorted.filter(s => !preserved.includes(s)).slice(0, need);
      return [...preserved, ...fillers].slice(0, 3);
    });
  }, [suppliersForYearSorted]);

  const handleSupplierChange = (index, value) => {
    const updated = [...selectedSuppliers];
    updated[index] = value || "";
    setSelectedSuppliers(updated);
  };

  const handleSearchChange = (index, value) => {
    const updated = [...searchTerms];
    updated[index] = value;
    setSearchTerms(updated);
  };

  const processedData = useMemo(() => {
    const suppliers = selectedSuppliers.filter(Boolean);

    // Determine 52/53 by scanning selected and previous year
    const maxWeek = (() => {
      const weeks = new Set();
      for (const r of data) {
        const rok = parseInt(r.Rok);
        if (rok === selectedYear || rok === selectedYear - 1) {
          const w = getISOWeek(r.DataZakupu);
          if (w) weeks.add(w);
        }
      }
      const m = Math.max(1, ...Array.from(weeks));
      return Math.min(Math.max(m, 52), 53);
    })();

    const weeklyData = [];
    for (let week = 1; week <= maxWeek; week++) {
      const entry = { week: `Tydzień ${week}` };

      suppliers.forEach(name => {
        const current = data
          .filter(d =>
            d.NazwaDostawcy === name &&
            parseInt(d.Rok) === selectedYear &&
            getISOWeek(d.DataZakupu) === week
          )
          .reduce((sum, d) => {
            const val = (Number(d.K_42) || 0) + (Number(d.K_43) || 0);
            return val > 0 ? sum + val : sum; // only positive
          }, 0);

        const previous = data
          .filter(d =>
            d.NazwaDostawcy === name &&
            parseInt(d.Rok) === selectedYear - 1 &&
            getISOWeek(d.DataZakupu) === week
          )
          .reduce((sum, d) => {
            const val = (Number(d.K_42) || 0) + (Number(d.K_43) || 0);
            return val > 0 ? sum + val : sum; // only positive
          }, 0);

        entry[`${name}_current`] = current;
        entry[`${name}_previous`] = previous;
      });

      weeklyData.push(entry);
    }

    return weeklyData;
  }, [data, selectedSuppliers, selectedYear]);

  const colors = ["#9848c7", "#4bc0c0", "#f39c12"];
  const dynamicTitle = `Rozkład tygodniowy wartości zakupów dla 3 wybranych dostawców (${selectedYear} vs ${selectedYear - 1})`;

  // Options per picker: year-limited + search; keep selected visible
  const getOptionsForIndex = (i) => {
    const term = (searchTerms[i] || "").toLocaleLowerCase("pl");
    const base = term
      ? suppliersForYearSorted.filter(s => s.toLocaleLowerCase("pl").includes(term))
      : suppliersForYearSorted;
    const selected = selectedSuppliers[i];
    const hasSelected = selected && base.includes(selected);
    return hasSelected || !selected ? base : [selected, ...base];
  };

  return (
    <Wrapper>
      <InfoIconWrapper data-tooltip-id="weekly-purchase-info">
        <Info />
      </InfoIconWrapper>
      <InfoTooltip
        id="weekly-purchase-info"
        place="left"
        content={
          <>
            <strong>Rozkład tygodniowy zakupów</strong><br />
            Porównanie tygodniowej wartości zakupów<br />
            dla wybranych dostawców – r/r (ISO tydzień).
          </>
        }
      />

      <Filters>
        <Picker>
          <Label>Rok:</Label>
          <Select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))}>
            {(yearsWithData.length ? yearsWithData : allYears).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </Select>
        </Picker>

        {[0, 1, 2].map((i) => (
          <Picker key={i}>
            <Label>Dostawca {i + 1}:</Label>
            <SearchInput
              type="text"
              placeholder="Szukaj dostawcy…"
              value={searchTerms[i]}
              onChange={(e) => handleSearchChange(i, e.target.value)}
              aria-label={`Szukaj dostawcy ${i + 1}`}
            />
            <Select
              value={selectedSuppliers[i] || ""}
              onChange={(e) => handleSupplierChange(i, e.target.value)}
            >
              <option value="">— wybierz —</option>
              {getOptionsForIndex(i).map((s) => (
                <option key={`${i}-${s}`} value={s}>{s}</option>
              ))}
            </Select>
          </Picker>
        ))}
      </Filters>

      <Title>{dynamicTitle}</Title>

      {suppliersForYearSorted.length === 0 ? (
        <EmptyState>Brak dostawców z wartościami &gt; 0 dla roku {selectedYear}.</EmptyState>
      ) : (
        <ResponsiveContainer width="100%" height={450}>
          <LineChart data={processedData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="week" interval={3} />
            <YAxis tickFormatter={(v) => v.toLocaleString("pl-PL")} domain={[0, 'auto']} />
            <Tooltip formatter={(v) => v.toLocaleString("pl-PL")} />
            <Legend />
            {selectedSuppliers.filter(Boolean).map((name, i) => (
              <React.Fragment key={name}>
                <Line
                  type="monotone"
                  dataKey={`${name}_current`}
                  stroke={colors[i % colors.length]}
                  name={`${name} (${selectedYear})`}
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey={`${name}_previous`}
                  stroke={colors[i % colors.length]}
                  name={`${name} (${selectedYear - 1})`}
                  strokeDasharray="5 5"
                  strokeWidth={1.5}
                  dot={false}
                />
              </React.Fragment>
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </Wrapper>
  );
};

export default WeeklyPurchaseByContractorChart;
